import React, { FC } from 'react';
import { AllieColors } from '../../../common/color.types';
import { StationSummaryStyled } from './StationSummaryCard.styles';
import { HappyFaceIcon, NeutralFaceIcon, SadFaceIcon } from '../../../styling/icons/svg';
import {
  AllieButton,
  AllieCard,
  AllieChip,
  AllieItem,
  AllieText,
  MulticolorProgressBar,
} from '../../../components';

type Health = 'good' | 'neutral' | 'bad';

export interface StationSummaryCardProps {
  title: string;
  data: {
    label: string;
    value: string;
    health?: Health;
  }[];
  barData: {
    value: number;
    color: AllieColors;
  }[];
  labels: {
    pattern: string;
    button: string;
    noProductiveStation: string;
    productionOrder: string;
    unstarted: string;
  };
  onClick: () => void;
  className?: string;
  isProductive?: boolean;
  lastSectionType: string;
  production: {
    status: string;
    legacyId?: string;
  }
}

const HEALTH_FACES: Record<Health, React.ReactNode> = {
  'bad': <SadFaceIcon />,
  'neutral': <NeutralFaceIcon />,
  'good': <HappyFaceIcon />,
}

export const AllieStationSummaryCard: FC<StationSummaryCardProps> = ({
  title,
  data,
  barData,
  labels,
  onClick,
  className,
  isProductive,
  lastSectionType,
  production,
}) => {
  const dataValueStatistics = (label: string, value: string, health?: Health, index?: number) => (
    <div style={{ display: 'flex' }}>
      <div key={`${label}-${value}-${index}`}>
        <div className="container-stats__item-label">
          <AllieText color="neutral-50" type="body2">
            {label}
          </AllieText>
        </div>
        <AllieText type="button-normal">{value}</AllieText>
      </div>
      {health && <div style={{ padding: 10 }}>{HEALTH_FACES[health]}</div>}
    </div>
  );

  return (
    <StationSummaryStyled className={className}>
      <AllieCard
        className='station-card'
        title={{ children: title }}
        subtitle={{ ...(!isProductive && { children: labels.noProductiveStation }) }}
        content={{
          children: (
            <>
              <hr className={`divider ${lastSectionType}`} />
              <div className="container">
                <div className="container-po-item">
                  <AllieItem style={{ height: 40 }}>
                    <div slot='start'>
                      <AllieText color="neutral-50" type="body2" weight={400}>
                        {`${labels.productionOrder}: ${production.legacyId || labels.unstarted}`}
                      </AllieText>
                    </div>
                    {
                      production.legacyId && (
                        <div slot='end'>
                          <AllieChip
                            color={{ backgroundColor: 'neutral-10' }}
                            style={{ borderRadius: 16, cursor: 'auto' }}
                          >
                            <AllieText color="neutral-70" type="body2" weight={500}>
                              {production.status}
                            </AllieText>
                          </AllieChip>
                        </div>
                      )
                    }
                  </AllieItem>
                </div>
                <div className="container-stats">
                  {data.map(({ label, value, health }, index) => 
                    dataValueStatistics(label, value, health, index)
                  )}
                </div>
                <div className="container-stats__stops-pattern">
                  <div className="label">
                    <AllieText color="neutral-50" type="body2">
                      {labels.pattern}
                    </AllieText>
                  </div>
                  <MulticolorProgressBar values={barData} />
                </div>
              </div>
            </>
          ),
        }}
        footer={{
          children: (
            <div>
              <AllieButton
                type="button"
                allieType="secondary"
                allieColor="neutral"
                fill="outline"
                full
                className="see-detail"
                onClick={onClick}
              >
                {labels.button}
              </AllieButton>
            </div>
          ),
        }}
      />
    </StationSummaryStyled>
  );
};
